import { useState } from 'react'
import { api } from '../services/api'

interface AppOverlayProps {
  appId: string
  sessionId: string
  overlayConfig?: {
    show_feedback?: boolean
    feedback_prompt?: string
  } | null
}

type OverlayState = 'collapsed' | 'expanded' | 'minimized' | 'submitted'

export function AppOverlay({ appId, sessionId, overlayConfig }: AppOverlayProps) {
  const [state, setState] = useState<OverlayState>('collapsed')
  const [rating, setRating] = useState<number>(0)
  const [comment, setComment] = useState('')
  const [submitting, setSubmitting] = useState(false)

  if (!overlayConfig?.show_feedback) return null

  const handleSubmit = async () => {
    if (rating === 0) return
    setSubmitting(true)

    try {
      await api.post('/feedback', {
        application_id: appId,
        session_id: sessionId,
        rating,
        comment: comment || undefined,
      })
      setState('submitted')
    } catch (err) {
      console.error('Failed to submit feedback:', err)
    } finally {
      setSubmitting(false)
    }
  }

  // Minimized dot
  if (state === 'minimized') {
    return (
      <button
        onClick={() => setState('collapsed')}
        className="fixed bottom-4 right-4 z-[150] w-3 h-3 rounded-full bg-[var(--color-text-tertiary)] hover:bg-[var(--color-text-secondary)] transition-colors"
        title="Show feedback"
      />
    )
  }

  // Submitted confirmation
  if (state === 'submitted') {
    return (
      <div className="fixed bottom-4 right-4 z-[150] bg-[var(--color-bg)]/90 backdrop-blur-sm border border-[var(--color-border)] rounded-lg px-4 py-3">
        <p className="text-sm text-[var(--color-text-secondary)]">Thanks for your feedback!</p>
      </div>
    )
  }

  // Collapsed pill
  if (state === 'collapsed') {
    return (
      <div className="fixed bottom-4 right-4 z-[150] flex items-center gap-2">
        <button
          onClick={() => setState('expanded')}
          className="bg-[var(--color-bg)]/90 backdrop-blur-sm border border-[var(--color-border)] rounded-lg px-4 py-2 text-sm text-[var(--color-text-secondary)] hover:text-[var(--color-text)] hover:border-[var(--color-text-tertiary)] transition-colors cursor-pointer"
        >
          {overlayConfig.feedback_prompt || 'Feedback'}
        </button>
        <button
          onClick={() => setState('minimized')}
          className="text-[var(--color-text-tertiary)] hover:text-[var(--color-text-secondary)] text-xs"
          title="Minimize"
        >
          &times;
        </button>
      </div>
    )
  }

  // Expanded panel
  return (
    <div className="fixed bottom-4 right-4 z-[150] w-80 bg-[var(--color-bg)]/95 backdrop-blur-sm border border-[var(--color-border)] rounded-lg p-4">
      <div className="flex items-center justify-between mb-3">
        <span className="text-sm font-medium">Quick Feedback</span>
        <button
          onClick={() => setState('collapsed')}
          className="text-[var(--color-text-tertiary)] hover:text-[var(--color-text)] text-sm"
        >
          &times;
        </button>
      </div>

      {/* Star rating */}
      <div className="flex gap-1 mb-3">
        {[1, 2, 3, 4, 5].map(star => (
          <button
            key={star}
            onClick={() => setRating(star)}
            className={`text-2xl transition-colors ${
              star <= rating
                ? 'text-yellow-400'
                : 'text-[var(--color-border)] hover:text-yellow-400/50'
            }`}
          >
            &#9733;
          </button>
        ))}
      </div>

      {/* Comment */}
      <textarea
        value={comment}
        onChange={e => setComment(e.target.value)}
        placeholder="Any thoughts? (optional)"
        rows={2}
        className="input w-full text-sm mb-3 resize-none"
      />

      {/* Submit */}
      <button
        onClick={handleSubmit}
        disabled={rating === 0 || submitting}
        className="btn btn-primary w-full text-sm"
      >
        {submitting ? 'Sending...' : 'Submit'}
      </button>
    </div>
  )
}
